import {
  ShippingOptionParcel,
  ShippingEstimate,
  hasItemsWithoutDimensions,
} from '@pedix-workspace/utils';
import { ShippingCostRequest } from './delivery.types';
import {
  createMinimumPurchaseErrorResponse,
  createToBeDefinedResponse,
  createPostalCodeRequiredResponse,
  createParcelShippingResponse,
  createParcelShippingSelectionRequiredResponse,
} from './shipping-cost-response-creators';
import { fetchParcelShippingEstimates } from './fetch-delivery-estimates';
import { ShippingCostResponse } from './shipping-cost-response.types';

export async function calculateParcelShippingCost(
  shippingOptionParcel: ShippingOptionParcel,
  shippingCostRequest: ShippingCostRequest,
): Promise<ShippingCostResponse | null> {
  const { address, selectedParcelOption, totalAmountWithDiscount } = shippingCostRequest;
  const { minimumPurchase, minimumPurchaseForFreeShipping } = shippingOptionParcel;

  if (!address?.addressDetails) {
    // Not enough information to calculate shipping cost
    return null;
  }

  // Verificar si falta código postal para paquetería
  if (!address.addressDetails.postalCode) {
    return createPostalCodeRequiredResponse(shippingOptionParcel);
  }

  // Verificar mínimo de compra
  if (isMinimumPurchaseNotMet(totalAmountWithDiscount, minimumPurchase)) {
    return createMinimumPurchaseErrorResponse(minimumPurchase, shippingOptionParcel);
  }

  // Verificar si hay productos sin medidas ANTES de hacer la llamada a la API
  if (hasItemsWithoutDimensions(shippingCostRequest.cartItems)) {
    // Si hay productos sin medidas, retornar "a convenir"
    return createToBeDefinedResponse(shippingOptionParcel);
  }

  // Si el usuario ya seleccionó una opción, usar esa
  if (selectedParcelOption) {
    const isFree = isFreeShippingGranted(totalAmountWithDiscount, minimumPurchaseForFreeShipping);
    return createParcelShippingResponse(selectedParcelOption, shippingOptionParcel, isFree);
  }

  // Obtener todas las opciones disponibles
  const parcelEstimates: ShippingEstimate[] = await fetchParcelShippingEstimates(
    shippingCostRequest.apiBaseUrl,
    shippingCostRequest.establishment,
    {
      cartItems: shippingCostRequest.cartItems,
      personalInformation: {
        name: shippingCostRequest.name,
        phone: shippingCostRequest.phone,
        address: shippingCostRequest.address,
      },
    },
  );

  // Separar estimates válidos y con errores
  const validEstimates = parcelEstimates.filter(estimate => !estimate.error);
  const errorEstimates = parcelEstimates.filter(estimate => !!estimate.error);

  if (validEstimates.length === 0 && errorEstimates.length > 0) {
    return createToBeDefinedResponse(shippingOptionParcel);
  }

  if (validEstimates.length === 0) {
    // No hay opciones disponibles
    return null;
  }

  // Verificar si califica para envío gratis
  const isFree = isFreeShippingGranted(totalAmountWithDiscount, minimumPurchaseForFreeShipping);

  if (validEstimates.length === 1) {
    // Solo una opción, seleccionar automáticamente
    return createParcelShippingResponse(validEstimates[0], shippingOptionParcel, isFree);
  }

  // Múltiples opciones disponibles
  if (isFree) {
    // Si es envío gratis, auto-seleccionar la opción más barata
    const cheapestEstimate = validEstimates.reduce((cheapest, current) =>
      current.price < cheapest.price ? current : cheapest,
    );
    return createParcelShippingResponse(cheapestEstimate, shippingOptionParcel, true);
  }

  // No es envío gratis, requiere selección del usuario
  return createParcelShippingSelectionRequiredResponse(shippingOptionParcel, validEstimates);
}

// Funciones de utilidad para validación
const isMinimumPurchaseNotMet = (total: number, minimum?: number | null) =>
  minimum ? total < minimum : false;

const isFreeShippingGranted = (total: number, minimum?: number | null) =>
  minimum ? total >= minimum : false;
